import React, { useCallback, useContext, useMemo, useState } from 'react';
import V3ContentScreenContext from './V3ContentScreenContext';
import Button from '../../../kingpin/atoms/Button';
import usePopup from '../../../hooks/usePopup';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { contentTypeCheckers } from '../ContentIndex/useContent';
import STORE from '../../../store';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import Form from '../../../kingpin/forms/Form';
import FormHeader from '../../../kingpin/forms/FormHeader';
import FormContent from '../../../kingpin/forms/FormContent';
import Row from 'components/Common/Row';
import _ from 'lodash';
import ToastContext from '../../../contexts/ToastContext';
import Tooltip from '../../../components/Tooltip';
import UsersContext from '../../../contexts/UsersContext';
import { GroupsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';
import userTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UsersProvider/userTypeCheckers';
import Dropdown from '../../../components/Inputs/Dropdown';
import FormInput from '../../../kingpin/forms/FormInput';
import isDefined from '../../../isDefined';
import ListItemWrapper from '../../DataManager/DatasetDefinitions/ListItemWrapper';
import UserIcon from '../../../components/Common/UserIcon';
import Typography from '../../../kingpin/atoms/Typography';
import Icon from '../../../kingpin/atoms/Icon';
import Inputs from '../../../components/Inputs';
import FeatureGate, { FEATURE_GATES } from '../../../components/FeatureGate';
import Alert from '../../../kingpin/Alert';
import V3GroupNameWithMembershipPopup from './V3GroupNameWithMembershipPopup';

const UsersDropdown = ({
  access,
  setAccess,
}: {
  access: V3Access;
  setAccess: React.Dispatch<React.SetStateAction<V3Access>>;
}) => {
  const { signedUpAndPendingUsers } = useContext(UsersContext);

  const availableUsers = useMemo((): (
    | UserManagement.SignedUpUser
    | UserManagement.PendingUser
  )[] => {
    return signedUpAndPendingUsers;
  }, [signedUpAndPendingUsers]);

  const userOptions = useMemo((): DropdownOption[] => {
    return availableUsers.map((u) => {
      return {
        key: u.id,
        label: u.displayName,
        isSelected: access.userIds.some((uid) => uid === u.id),
        onSelected: () => {
          setAccess((current) => {
            const isSelected = current.userIds.some((uid) => uid === u.id);
            if (!isSelected) {
              return {
                ...current,
                userIds: [...current.userIds, u.id],
              };
            }
            return {
              ...current,
              userIds: current.userIds.filter((uid) => uid !== u.id),
            };
          });
        },
      };
    });
  }, [access.userIds, availableUsers, setAccess]);

  return (
    <Dropdown
      testId={'select-users'}
      options={userOptions}
      isMulti
      isSearchEnabled
      size={'Medium'}
      placeholder={'Select Users...'}
    />
  );
};

const GroupsDropdown = ({
  access,
  setAccess,
}: {
  access: V3Access;
  setAccess: React.Dispatch<React.SetStateAction<V3Access>>;
}) => {
  const { groups } = useContext(GroupsContext);

  const availableGroups = useMemo((): UserManagement.Group[] => {
    return groups;
  }, [groups]);

  const groupOptions = useMemo((): DropdownOption[] => {
    return availableGroups.map((g) => {
      return {
        key: g.id,
        label: g.name,
        isSelected: access.groupIds.some((gid) => gid === g.id),
        onSelected: () => {
          setAccess((current) => {
            const isSelected = current.groupIds.some((gid) => gid === g.id);
            if (!isSelected) {
              return {
                ...current,
                groupIds: [...current.groupIds, g.id],
              };
            }
            return {
              ...current,
              groupIds: current.groupIds.filter((gid) => gid !== g.id),
            };
          });
        },
      };
    });
  }, [access.groupIds, availableGroups, setAccess]);

  return (
    <Dropdown
      options={groupOptions}
      isMulti
      isSearchEnabled
      size={'Medium'}
      placeholder={'Select Groups...'}
      testId={'select-groups'}
    />
  );
};

const SharedWith = ({
  access,
  setAccess,
}: {
  access: V3Access;
  setAccess: React.Dispatch<React.SetStateAction<V3Access>>;
}) => {
  const { signedUpAndPendingUsers } = useContext(UsersContext);
  const { groups } = useContext(GroupsContext);
  const sharedGroups = useMemo((): UserManagement.Group[] => {
    return _.sortBy(
      access.groupIds
        .map((gid) => groups.find((g) => g.id === gid))
        .filter(isDefined),
      'name',
    );
  }, [groups, access.groupIds]);
  const sharedUsers = useMemo((): (
    | UserManagement.SignedUpUser
    | UserManagement.PendingUser
  )[] => {
    return _.sortBy(
      access.userIds
        .map((uid) => signedUpAndPendingUsers.find((u) => u.id === uid))
        .filter(isDefined)
        .filter(userTypeCheckers.isSignedUpOrPendingUser),
      'displayName',
    );
  }, [signedUpAndPendingUsers, access.userIds]);

  const removeUser = useCallback(
    (user: UserManagement.SignedUpUser | UserManagement.PendingUser) => {
      setAccess((current) => ({
        ...current,
        userIds: current.userIds.filter((uid) => uid !== user.id),
      }));
    },
    [setAccess],
  );

  const removeGroup = useCallback(
    (group: UserManagement.Group) => {
      setAccess((current) => ({
        ...current,
        groupIds: current.groupIds.filter((gid) => gid !== group.id),
      }));
    },
    [setAccess],
  );

  if (sharedGroups.length === 0 && sharedUsers.length === 0) {
    return <Typography.Body type={'Body 14'}>Nobody</Typography.Body>;
  }

  return (
    <>
      {sharedGroups.map((group) => (
        <ListItemWrapper
          key={group.id}
          style={{ justifyContent: 'space-between' }}
        >
          <Row centerAlign style={{ gap: 4 }}>
            <div
              style={{
                width: 24,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon icon={'group'} width={12} height={12} />
            </div>
            <V3GroupNameWithMembershipPopup group={group} />
          </Row>
          <Button
            size={'Small'}
            type={'Ghost'}
            icon={'cross'}
            onClick={() => removeGroup(group)}
          />
        </ListItemWrapper>
      ))}
      {sharedUsers.map((user) => (
        <ListItemWrapper
          key={user.id}
          style={{ justifyContent: 'space-between' }}
        >
          <Row centerAlign style={{ gap: 4 }}>
            <div
              style={{
                width: 24,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UserIcon {...user} />
            </div>
            <Typography.Body type={'Button Text'}>
              {user.displayName}
            </Typography.Body>
          </Row>
          <Button
            size={'Small'}
            type={'Ghost'}
            icon={'cross'}
            onClick={() => removeUser(user)}
          />
        </ListItemWrapper>
      ))}
    </>
  );
};

const PublicToggle = ({
  access,
  setAccess,
}: {
  access: V3Access;
  setAccess: React.Dispatch<React.SetStateAction<V3Access>>;
}) => {
  const isPublic = useMemo(() => {
    return access.isPublic;
  }, [access.isPublic]);

  const onToggled = useCallback(() => {
    setAccess((current) => ({
      ...current,
      isPublic: !current.isPublic,
    }));
  }, [setAccess]);

  return (
    <Row style={{ gap: 8 }}>
      <Inputs.Toggle value={isPublic} onChange={onToggled} />
      <Typography.Body type={'Label'}>Public</Typography.Body>
    </Row>
  );
};

export const ShareContentPopup = ({
  onSave,
  initialAccess,
  close,
}: {
  onSave: (newAccess: V3Access) => Promise<void>;
  initialAccess: V3Access;
  close: () => void;
}) => {
  const { showToast } = useContext(ToastContext);
  const [access, setAccess] = useState<V3Access>(initialAccess);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isValid = useMemo(() => {
    return !_.isEqual(access, initialAccess) && !isSaving;
  }, [access, initialAccess, isSaving]);

  const onShareClicked = useCallback(() => {
    if (isSaving || !isValid) {
      return;
    }

    setIsSaving(true);
    onSave(access).then(() => {
      showToast('Access Updated');
      close();
    });
  }, [access, close, isSaving, isValid, onSave, showToast]);

  const Content = useMemo(() => {
    if (access.isPublic) {
      return (
        <Alert
          type={'Info'}
          title={'This asset is public'}
          body={
            'This asset is available to everyone and will appear in their all assets'
          }
        />
      );
    }

    return (
      <>
        <FormInput label={'Users'}>
          <UsersDropdown access={access} setAccess={setAccess} />
        </FormInput>
        <FormInput label={'Groups'}>
          <GroupsDropdown access={access} setAccess={setAccess} />
        </FormInput>
        <FormInput label={'Shared With'}>
          <SharedWith access={access} setAccess={setAccess} />
        </FormInput>
      </>
    );
  }, [access]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader
          title={'Share Asset'}
          onClose={close}
          titleExtra={
            <Row style={{ justifyContent: 'flex-end', flex: 1, width: '100%' }}>
              <FeatureGate featureName={FEATURE_GATES.V3_PUBLIC_CONTENT}>
                <PublicToggle access={access} setAccess={setAccess} />
              </FeatureGate>
            </Row>
          }
        />
        <FormContent>{Content}</FormContent>
        <Row spaceBetween centerAlign>
          <div />
          <Button
            size={'Medium'}
            type={'Primary'}
            onClick={onShareClicked}
            isLoading={isSaving}
            isDisabled={!isValid}
            label="Update"
          />
        </Row>
      </Form>
    </Modal>
  );
};

const V3ContentShareButton = ({ v3Access }: { v3Access: V3Access }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { content } = useContext(V3ContentScreenContext);
  const { isOpen, open, close } = usePopup();

  const updateDashboardAccess = useCallback(
    (newAccess: V3Access, dashboard: DashboardType) => {
      const newDashboard: DashboardType = {
        ...dashboard,
        v3Access: newAccess,
      };

      return STORE.getDashboardsRef({ accountId: selectedAccountId })
        .doc(newDashboard.id)
        .set(newDashboard);
    },
    [selectedAccountId],
  );
  const updateReportAccess = useCallback(
    (newAccess: V3Access, report: ReportType) => {
      const newReport: ReportType = {
        ...report,
        v3Access: newAccess,
      };

      return STORE.getReportsRef({ accountId: selectedAccountId })
        .doc(newReport.id)
        .set(newReport);
    },
    [selectedAccountId],
  );
  const updateScorecardAccess = useCallback(
    (newAccess: V3Access, scorecard: Scorecards.Scorecard) => {
      const newScorecard: Scorecards.Scorecard = {
        ...scorecard,
        v3Access: newAccess,
      };

      return STORE.visualisations
        .getScorecardsRef({ accountId: selectedAccountId })
        .doc(newScorecard.id)
        .set(newScorecard);
    },
    [selectedAccountId],
  );
  const updateGoalAccess = useCallback(
    (newAccess: V3Access, goal: GeneralGoal) => {
      const newGoal: GeneralGoal = {
        ...goal,
        v3Access: newAccess,
      };

      return STORE.visualisations
        .getGoalsRef({ accountId: selectedAccountId })
        .doc(newGoal.id)
        .set(newGoal);
    },
    [selectedAccountId],
  );

  const onSave = useCallback(
    (newAccess: V3Access): Promise<void> => {
      if (!content) {
        const e = new Error();
        e.name = 'Content not defined';
        throw e;
      }

      if (contentTypeCheckers.isDashboard(content)) {
        return updateDashboardAccess(newAccess, content.dashboard);
      } else if (contentTypeCheckers.isReport(content)) {
        return updateReportAccess(newAccess, content.report);
      } else if (contentTypeCheckers.isGoal(content)) {
        return updateGoalAccess(newAccess, content.goal);
      } else if (contentTypeCheckers.isScorecard(content)) {
        return updateScorecardAccess(newAccess, content.scorecard);
      } else {
        const e = new Error();
        e.name = 'Unexpected content type';
        throw e;
      }
    },
    [
      content,
      updateDashboardAccess,
      updateGoalAccess,
      updateReportAccess,
      updateScorecardAccess,
    ],
  );

  return (
    <>
      <Tooltip content={'Share'}>
        <Button
          icon={'share'}
          onClick={open}
          type={'Primary'}
          size="Small"
          testId={'share-button'}
        />
      </Tooltip>
      <ModalTransition>
        {isOpen && (
          <ShareContentPopup
            close={close}
            onSave={onSave}
            initialAccess={v3Access}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default V3ContentShareButton;
